import { refundRuleText } from "./billing-copy.js";
import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Request a refund" — unused purchased credit back to the person who bought it (plan-014 unit **D6** over D3b's
 * routes `GET /v1/billing-accounts/{id}/refundable` and `POST …/refund-requests`; OD-13; design §27.8.16).
 *
 * The dialog shows what the server says is refundable (only unused PURCHASED credit: WeTalk's credit, promotions and
 * the free trial are used first and never refunded — the server's own rule, printed), takes an amount (all of it by
 * default) and sends one request with an Idempotency-Key minted when the dialog opens, so a double click or a retry is
 * the same request. Nothing moves until a WeTalk finance operator approves it; the copy says so, and says where the
 * money goes (the card it came from, the rest by bank transfer — D3b's plan). Mounted only while open.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { money, type CurrencyCode } from "@wetalk/domain";
import { Button, Dialog, Field, Input } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import type { RefundRequestView } from "../../api/slice/financial-document.js";
import { formatMoney } from "../../money.js";

export const REFUND_COPY = {
  get title() { return portalText("finance", "m1210"); },
  get approval() { return portalText("finance", "m1211"); },
  get nothing() { return portalText("finance", "m1212"); },
  sent: (amount: string) => portalText("finance", "refund.sent", { amount }),
  get amountHint() { return portalText("finance", "m1213"); },
  get policyClosed() { return portalText("finance", "m1214"); },
} as const;

/** `"12.40"` → 1240; anything else (more than two decimals, a sign, letters) → `null`. Integer arithmetic only. */
export function minorOfEuroText(text: string): number | null {
  const match = /^(\d{1,7})(?:[.,](\d{1,2}))?$/.exec(text.trim());
  if (match === null) return null;
  const whole = Number(match[1]);
  const cents = match[2] === undefined ? 0 : Number(match[2].padEnd(2, "0"));
  return whole * 100 + cents;
}

/** 1240 → `"12.40"` for the amount field. */
export function euroTextOf(minor: number): string {
  return `${String(Math.floor(minor / 100))}.${String(minor % 100).padStart(2, "0")}`;
}

function newIdempotencyKey(): string {
  return `refund-${globalThis.crypto.randomUUID()}`;
}

export function RefundDialog(props: { readonly open: boolean; readonly billingAccountId: string; readonly onClose: () => void }): ReactNode {
  usePortalTranslation("finance");
  if (!props.open) return null;
  return <RefundDialogOpen billingAccountId={props.billingAccountId} onClose={props.onClose} />;
}

function RefundDialogOpen(props: { readonly billingAccountId: string; readonly onClose: () => void }): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [amountText, setAmountText] = useState<string | null>(null);
  const [problem, setProblem] = usePortalMessage();
  const [sent, setSent] = useState<RefundRequestView | null>(null);

  const refundable = useQuery({
    queryKey: ["refundable", props.billingAccountId],
    queryFn: () => gateway.readRefundable(props.billingAccountId),
  });
  const send = useMutation({
    mutationFn: (amountMinor: number | null) => gateway.requestRefund(props.billingAccountId, amountMinor, idempotencyKey),
    onSuccess: async (made) => {
      setSent(made);
      await queryClient.invalidateQueries({ queryKey: ["refundable", props.billingAccountId] });
    },
    onError: (error: unknown) => setProblem(() => (apiProblemText(error, portalText("finance", "m0330")))),
  });

  const data = refundable.data;
  const currency = (data?.currency ?? "EUR") as CurrencyCode;
  const shownText = amountText ?? (data === undefined ? "" : euroTextOf(data.refundableMinor));

  const submit = (): void => {
    if (data === undefined) return;
    const asked = minorOfEuroText(shownText);
    if (asked === null || asked < 1 || asked > data.refundableMinor) {
      setProblem(() => (portalText("finance", "m0331", { value1: euroTextOf(data.refundableMinor) })));
      return;
    }
    setProblem(null);
    send.mutate(asked === data.refundableMinor ? null : asked);
  };

  return (
    <Dialog
      open
      title={REFUND_COPY.title}
      onClose={props.onClose}
      actions={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            {sent === null ? portalText("finance", "m0106") : portalText("finance", "m0107")}
          </Button>
          {sent === null && data !== undefined && data.refundable ? (
            <Button variant="primary" disabled={send.isPending} onClick={submit}>
              {send.isPending ? portalText("finance", "m0317") : portalText("finance", "m0332")}
            </Button>
          ) : null}
        </>
      }
    >
      {sent !== null ? (
        <p className="c-note" role="status">
          {REFUND_COPY.sent(formatMoney(money(sent.amountMinor, sent.currency as CurrencyCode)))}
        </p>
      ) : refundable.isPending ? (
        <p className="c-note" role="status">{portalText("finance", "m0333")}</p>
      ) : refundable.isError || data === undefined ? (
        <p className="c-note" role="status">
          {apiProblemText(refundable.error, portalText("finance", "m0334"))}
        </p>
      ) : !data.refundable ? (
        <>
          <p className="c-note">{data.refundableMinor === 0 ? REFUND_COPY.nothing : REFUND_COPY.policyClosed}</p>
          <p className="c-note">{refundRuleText(data.rule)}</p>
        </>
      ) : (
        <>
          <p className="c-note">
            {formatMoney(money(data.refundableMinor, currency))}{portalText("finance", "m0335")}{refundRuleText(data.rule)}
          </p>
          <Field label={portalText("finance", "m0336")} description={REFUND_COPY.amountHint} {...(problem === null ? {} : { error: problem })}>
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                value={shownText}
                onChange={(event) => {
                  const next = event.currentTarget.value;
                  setAmountText(next);
                }}
              />
            )}
          </Field>
          <p className="c-note">{REFUND_COPY.approval}</p>
        </>
      )}
    </Dialog>
  );
}
